/**
 * PricingFields — unit price / bulk price / bulk threshold inputs (EPIC-006
 * Phase 7, Task 7.2).
 *
 * Money is Decimal KGS (no tiyin). The raw form value stays a plain string
 * (e.g. "1234.56") so no float math runs before the API boundary; the display
 * shows the RU-grouped form ("1 234,56") while the field is blurred and the
 * raw editable form while focused. Validation errors arrive as i18n keys from
 * the Zod schema (resolved at the call site) and render under each field via
 * `--g-error`.
 *
 * Presentational: the editor owns RHF and passes value/onChange/error per
 * field. `aria-invalid` + `aria-describedby` wire the error to the input.
 */
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
} from "@/features/seller-dashboard/api";
import { fmtNum } from "@/lib/format";
import { parsePriceInput } from "@/features/seller-dashboard/schemas";

/** A raw price string → RU display ("1234.56" → "1 234,56"); "" stays "". */
function toDisplay(raw: string): string {
  const n = parsePriceInput(raw);
  if (n === null || Number.isNaN(n)) return raw;
  const fixed = n.toFixed(2);
  const [intPart, decPart] = fixed.split(".");
  return `${fmtNum(Number(intPart))},${decPart}`;
}

interface MoneyInputProps {
  id: string;
  label: string;
  hint?: string;
  unit: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  testId: string;
  /** SCRUM-167b: renders a required-field asterisk on the label and wires
   * `aria-required` on the input. Affordance only — RHF/Zod (not the
   * browser) still owns validation, so this deliberately does NOT set the
   * native `required` attribute, which would trigger the browser's own
   * validation bubble instead of the form's inline error. */
  required?: boolean;
}

function MoneyInput({
  id,
  label,
  hint,
  unit,
  value,
  onChange,
  error,
  testId,
  required,
}: MoneyInputProps): ReactElement {
  // Local editing buffer: while focused the user sees their raw keystrokes;
  // while blurred the canonical `value` is shown RU-formatted. Decoupling the
  // displayed text from the stored value keeps the controlled input stable
  // (no format-on-keystroke feedback loop).
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(value);
  const display = focused ? draft : toDisplay(value);
  const errorId = `${id}-error`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-[var(--g-error)]">
            {" "}
            *
          </span>
        ) : null}
      </label>
      <div
        className={[
          "flex min-h-[44px] items-center rounded-[var(--g-radius)] border bg-card px-3",
          error ? "border-[var(--g-error)]" : "border-border",
        ].join(" ")}
      >
        <input
          id={id}
          data-testid={testId}
          inputMode="decimal"
          value={display}
          onFocus={() => {
            setDraft(value);
            setFocused(true);
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => {
            setDraft(e.target.value);
            onChange(e.target.value);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          aria-required={required || undefined}
          className="w-full bg-transparent text-sm text-foreground outline-none"
        />
        <span className="ml-2 shrink-0 text-xs text-muted-foreground">{unit}</span>
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-[var(--g-error)]">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export interface PricingFieldsProps {
  price: string;
  bulkPrice: string;
  bulkThreshold: string;
  onPriceChange: (v: string) => void;
  onBulkPriceChange: (v: string) => void;
  onBulkThresholdChange: (v: string) => void;
  /** Resolved (translated) error messages, or undefined when valid. */
  priceError?: string;
  bulkPriceError?: string;
  bulkThresholdError?: string;
}

export function PricingFields({
  price,
  bulkPrice,
  bulkThreshold,
  onPriceChange,
  onBulkPriceChange,
  onBulkThresholdChange,
  priceError,
  bulkPriceError,
  bulkThresholdError,
}: PricingFieldsProps): ReactElement {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const unit = t("editor.pricing.unit");
  const thresholdErrorId = "bulk-threshold-error";
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="pricing-fields">
      <MoneyInput
        id="price_kgs"
        testId="price-input"
        label={t("editor.pricing.price")}
        hint={t("editor.pricing.priceHint")}
        unit={unit}
        value={price}
        onChange={onPriceChange}
        error={priceError}
        required
      />
      <MoneyInput
        id="bulk_price_kgs"
        testId="bulk-price-input"
        label={t("editor.pricing.bulkPrice")}
        unit={unit}
        value={bulkPrice}
        onChange={onBulkPriceChange}
        error={bulkPriceError}
      />
      <div className="flex flex-col gap-1">
        <label
          htmlFor="bulk_threshold"
          className="text-sm font-medium text-foreground"
        >
          {t("editor.pricing.bulkThreshold")}
        </label>
        <input
          id="bulk_threshold"
          data-testid="bulk-threshold-input"
          inputMode="numeric"
          value={bulkThreshold}
          onChange={(e) => onBulkThresholdChange(e.target.value)}
          aria-invalid={bulkThresholdError ? true : undefined}
          aria-describedby={bulkThresholdError ? thresholdErrorId : undefined}
          className={[
            "min-h-[44px] rounded-[var(--g-radius)] border bg-card px-3 text-sm text-foreground outline-none",
            bulkThresholdError ? "border-[var(--g-error)]" : "border-border",
          ].join(" ")}
        />
        {bulkThresholdError ? (
          <p
            id={thresholdErrorId}
            role="alert"
            className="text-xs text-[var(--g-error)]"
          >
            {bulkThresholdError}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("editor.pricing.bulkThresholdHint")}
          </p>
        )}
      </div>
    </div>
  );
}

export default PricingFields;
